import { useEffect, useState } from "react";

/**
 * SignedDocsAdminPanel — Panneau pour :
 *  1. uploader la signature (PNG à fond transparent) de chaque membre du Bureau
 *  2. générer les documents signés (Statuts / RI / PV / Liste Bureau)
 *  3. télécharger le ZIP final du « Dossier signé »
 */
export const SignedDocsAdminPanel = ({ API, pinInput }) => {
  const [members, setMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const pin = encodeURIComponent(pinInput || "SAFUAYE2024");

  const load = async () => {
    try {
      const r1 = await fetch(`${API}/togo/signatures?pin=${pin}`);
      const d1 = await r1.json();
      setMembers(d1.members || []);
      const r2 = await fetch(`${API}/togo/documents-signes/list?pin=${pin}`);
      const d2 = await r2.json();
      setFiles(d2.files || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(true); setStatus(`Envoi de la signature de ${key}…`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/togo/signatures/${key}?pin=${pin}`, { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text());
      setStatus(`✅ Signature ${key} enregistrée`);
      await load();
    } catch (e) { setStatus(`❌ ${e.message}`); }
    setBusy(false);
  };

  const removeSig = async (key) => {
    if (!window.confirm(`Supprimer la signature de ${key} ?`)) return;
    setBusy(true);
    try {
      await fetch(`${API}/togo/signatures/${key}?pin=${pin}`, { method: "DELETE" });
      setStatus(`🗑️ Signature ${key} supprimée`);
      await load();
    } catch (e) { setStatus(`❌ ${e.message}`); }
    setBusy(false);
  };

  const generate = async () => {
    setBusy(true); setStatus("Génération du dossier signé en cours…");
    try {
      const r = await fetch(`${API}/togo/documents-signes/generate?pin=${pin}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setStatus(`❌ ${d.error}`);
      else setStatus(`✅ ${d.generated?.length || 0} documents signés générés`);
      await load();
    } catch (e) { setStatus(`❌ ${e.message}`); }
    setBusy(false);
  };

  const uploadedCount = members.filter(m => m.uploaded).length;

  return (
    <div data-testid="admin-signed-docs-panel" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
          ✍️ Documents signés — Dossier officiel avec signatures
        </h3>
        <button
          onClick={load}
          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold"
          data-testid="admin-signed-refresh"
        >
          🔄 Rafraîchir
        </button>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-600 p-3 rounded text-xs text-blue-900">
        📖 <strong>Comment ça marche :</strong> uploadez une image PNG (fond transparent recommandé) de la signature manuscrite de chaque membre du Bureau. Vous pouvez photographier la signature sur la liste d'émargement des 25 participants, la détourer, puis l'uploader ici. Cliquez ensuite sur « Générer les documents signés » pour créer les versions des Statuts / RI / PV / Liste Bureau avec les signatures composées automatiquement.
      </div>

      {/* Grid des signatures */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <h4 className="text-sm font-bold text-gray-800">1️⃣ Signatures du Bureau ({uploadedCount}/{members.length})</h4>
          <div className="flex flex-col items-end">
            <button
              onClick={generate}
              disabled={busy || uploadedCount === 0}
              className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs px-4 py-2 rounded font-bold"
              data-testid="admin-signed-generate"
              title={uploadedCount === 0 ? "Uploadez au moins une signature avant de générer" : "Générer les PDF signés"}
            >
              {busy ? "⏳ En cours…" : "🖋️ Générer les documents signés"}
            </button>
            {uploadedCount === 0 && (
              <span className="text-[10px] text-red-600 mt-1" data-testid="admin-signed-help">
                ⚠️ Uploadez au moins 1 signature pour activer ce bouton
              </span>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {members.map(m => (
            <div key={m.key} className={`border rounded-lg p-3 ${m.uploaded ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-white"}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wider">{m.fonction}</div>
                  <div className="text-sm font-bold text-gray-900">{m.name}</div>
                </div>
                {m.uploaded && (
                  <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full">Uploaded ✓</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => upload(m.key, e.target.files?.[0])}
                  disabled={busy}
                  className="text-[11px] flex-1"
                  data-testid={`admin-sig-upload-${m.key}`}
                />
                {m.uploaded && (
                  <button
                    onClick={() => removeSig(m.key)}
                    disabled={busy}
                    className="text-[11px] bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded font-semibold"
                    data-testid={`admin-sig-delete-${m.key}`}
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Documents signés générés */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-800">2️⃣ Documents signés générés ({files.length})</h4>
          {files.length > 0 && (
            <a
              href={`${API}/togo/documents-signes/zip?pin=${pin}`}
              target="_blank" rel="noreferrer"
              className="bg-red-700 hover:bg-red-800 text-white text-xs px-4 py-2 rounded font-bold"
              data-testid="admin-signed-zip"
            >
              📦 Télécharger le ZIP complet
            </a>
          )}
        </div>
        {files.length === 0 ? (
          <div className="text-xs text-gray-500 italic">Aucun document signé pour le moment. Uploadez au moins une signature puis cliquez sur « Générer ».</div>
        ) : (
          <ul className="space-y-1">
            {files.map(f => (
              <li key={f.name} className="flex items-center justify-between text-xs bg-gray-50 hover:bg-gray-100 rounded px-2 py-1.5">
                <span className="flex-1 font-mono truncate">📄 {f.name}</span>
                <span className="text-gray-500 mx-2">{f.size_kb} KB</span>
                <a href={f.url} target="_blank" rel="noreferrer" className="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded font-semibold" data-testid={`admin-signed-view-${f.name}`}>Ouvrir</a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {status && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border-l-4 border-emerald-600 p-2 rounded" data-testid="admin-signed-status">
          {status}
        </div>
      )}
    </div>
  );
};

export default SignedDocsAdminPanel;
